import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Building2, ChevronRight, Contact, FileKey, KeyRound, Search, X } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useLabels } from '@/contexts/WorkspaceContext';
import { Segmented } from '@/pages/catalog/shared';
import { OwnerRow, TenantRow, realEstateService } from '@/services/real-estate.service';
import { formatDay, formatPrice, periodShort, useRealEstateAccess, withArticle } from './shared';

type Role = 'owners' | 'tenants';
const PAGE_TITLE = 'Owners & tenants';

const matches = (row: { name?: string; phone?: string; email?: string }, q: string) =>
  [row.name, row.phone, row.email].some((v) => (v ?? '').toLowerCase().includes(q));

export default function OwnersTenantsPage() {
  const t = useLabels();
  const navigate = useNavigate();
  const access = useRealEstateAccess();
  const [params, setParams] = useSearchParams();
  const role: Role = params.get('role') === 'tenants' ? 'tenants' : 'owners';

  const [owners, setOwners] = useState<OwnerRow[] | null>(null);
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [error, setError] = useState<Role | null>(null);
  const [query, setQuery] = useState('');
  const reqId = useRef(0);

  const load = useCallback(async (r: Role) => {
    const id = ++reqId.current;
    setError(null);
    try {
      if (r === 'owners') {
        const res = await realEstateService.owners();
        if (id === reqId.current) setOwners(res.items);
      } else {
        const res = await realEstateService.tenants();
        if (id === reqId.current) setTenants(res.items);
      }
    } catch {
      if (id === reqId.current) setError(r);
    }
  }, []);

  useEffect(() => { void load(role); }, [role, load]);

  const setRole = (r: Role) => {
    const p = new URLSearchParams(params);
    if (r === 'owners') p.delete('role');
    else p.set('role', r);
    setParams(p, { replace: true });
  };

  const q = query.trim().toLowerCase();
  const shownOwners = useMemo(() => (owners ?? []).filter((o) => !q || matches(o, q)), [owners, q]);
  const shownTenants = useMemo(() => (tenants ?? []).filter((x) => !q || matches(x, q)), [tenants, q]);
  const rows = role === 'owners' ? owners : tenants;
  const shownCount = role === 'owners' ? shownOwners.length : shownTenants.length;
  const withLease = (tenants ?? []).filter((x) => x.current).length;

  const person = (row: { contactUid: string; name?: string; phone?: string; email?: string }) => (
    <div className="min-w-0">
      {access.contactLinks ? (
        <Link to={`/contacts/${row.contactUid}`} className="block truncate text-[13.5px] font-semibold text-text-primary hover:text-primary hover:underline">{row.name || row.phone || 'Unnamed'}</Link>
      ) : (
        <div className="truncate text-[13.5px] font-semibold text-text-primary">{row.name || row.phone || 'Unnamed'}</div>
      )}
      <div className="truncate text-2xs tabular-nums text-text-muted">{[row.phone, row.email].filter(Boolean).join(' · ')}</div>
    </div>
  );

  return (
    <>
      <Topbar
        title={PAGE_TITLE}
        breadcrumb={`${t('operations')} / ${PAGE_TITLE}`}
        description={`The ${t('contacts').toLowerCase()} who own your units and the ones renting them.`}
      />

      <div className="w-full space-y-5 px-4 py-6 sm:px-7 sm:py-7">
        <div className="rounded-2xl border border-border bg-surface shadow-soft">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center">
            <Segmented<Role>
              value={role}
              onChange={setRole}
              size="sm"
              options={[
                { value: 'owners', label: owners ? `Owners (${owners.length})` : 'Owners', icon: <Building2 size={13} /> },
                { value: 'tenants', label: tenants ? `Tenants (${tenants.length})` : 'Tenants', icon: <KeyRound size={13} /> },
              ]}
            />
            <div className="w-full sm:ml-auto sm:max-w-xs">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, phone or email"
                iconLeft={<Search size={15} />}
                iconRight={query ? <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="hover:text-text-primary"><X size={14} /></button> : undefined}
                aria-label={`Search ${role}`}
              />
            </div>
          </div>

          {error === role ? (
            <LoadErrorState what={role} onRetry={() => void load(role)} bare />
          ) : rows === null ? (
            <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-4">
              {role === 'owners' ? (
                <EmptyState
                  icon={<Contact size={28} />}
                  title="No owners yet"
                  description={`Set the owner on ${withArticle(t('properties', 'singular').toLowerCase())} (or on a single unit) and they show up here with what they own.`}
                  action={<Button variant="secondary" onClick={() => navigate('/properties?view=properties')}>Go to {t('properties').toLowerCase()}</Button>}
                />
              ) : (
                <EmptyState
                  icon={<KeyRound size={28} />}
                  title="No tenants yet"
                  description={`Tenants show up here once you add ${withArticle(t('leases', 'singular').toLowerCase())} for them.`}
                  action={access.leases ? <Button onClick={() => navigate('/leases?new=1')}>New {t('leases', 'singular').toLowerCase()}</Button> : undefined}
                />
              )}
            </div>
          ) : shownCount === 0 ? (
            <div className="p-4"><NoMatchesState onClear={() => setQuery('')} description="No one matches that search." /></div>
          ) : role === 'owners' ? (
            <div>
              <div className="hidden grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,0.6fr))] gap-3 border-b border-border bg-surface-alt px-4 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted md:grid">
                <span>Owner</span><span className="text-right">{t('properties')}</span><span className="text-right">Units</span><span className="text-right">Rented</span><span className="text-right">Available</span>
              </div>
              <ul className="divide-y divide-border">
                {shownOwners.map((o) => (
                  <li key={o.contactUid} className="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,0.6fr))] md:items-center md:gap-3">
                    {person(o)}
                    <div className="grid grid-cols-4 gap-2 md:contents">
                      <Stat label={t('properties')} value={o.properties} />
                      <Stat label="Units" value={o.units} />
                      <Stat label="Rented" value={o.rented} tone={o.rented ? 'text-info' : undefined} />
                      <Stat label="Available" value={o.available} tone={o.available ? 'text-success' : undefined} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div>
              <div className="hidden grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,0.5fr)_20px] gap-3 border-b border-border bg-surface-alt px-4 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted md:grid">
                <span>Tenant</span><span>Current {t('leases', 'singular').toLowerCase()}</span><span className="text-right">{t('leases')}</span><span />
              </div>
              <ul className="divide-y divide-border">
                {shownTenants.map((x) => {
                  const href = x.current && access.leases ? `/leases?lease=${x.current.leaseUid}` : null;
                  return (
                    <li key={x.contactUid} className="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,0.5fr)_20px] md:items-center md:gap-3">
                      {person(x)}
                      <div className="min-w-0 text-xs">
                        {x.current ? (
                          <>
                            <div className="truncate font-medium text-text-primary">
                              {x.current.unitLabel ?? 'Unit'}{x.current.propertyName ? <span className="font-normal text-text-secondary"> · {x.current.propertyName}</span> : null}
                            </div>
                            <div className="truncate text-2xs tabular-nums text-text-muted">
                              {formatPrice(x.current.rentAmount, x.current.currency)}{periodShort(x.current.rentPeriod)} · ends {formatDay(x.current.endDate)}
                            </div>
                          </>
                        ) : (
                          <span className="text-text-muted">No active {t('leases', 'singular').toLowerCase()}</span>
                        )}
                      </div>
                      <div className="text-xs text-text-secondary md:text-right">
                        <span className="md:hidden text-text-muted">{t('leases')}: </span><span className="font-semibold tabular-nums text-text-primary">{x.leases}</span>
                      </div>
                      {href ? (
                        <Link to={href} aria-label={`Open ${t('leases', 'singular').toLowerCase()} for ${x.name ?? 'tenant'}`} className="hidden text-text-muted hover:text-primary md:block">
                          <ChevronRight size={16} />
                        </Link>
                      ) : <span className="hidden md:block" />}
                      {href && (
                        <Link to={href} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline md:hidden">
                          <FileKey size={12} /> Open {t('leases', 'singular').toLowerCase()}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
              {tenants && tenants.length > 0 && (
                <p className="border-t border-border px-4 py-2.5 text-2xs text-text-muted">{withLease} of {tenants.length} with an active {t('leases', 'singular').toLowerCase()}.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col text-xs md:block md:text-right">
      <span className="truncate text-2xs text-text-muted md:hidden">{label}</span>
      <span className={clsx('font-semibold tabular-nums', tone ?? 'text-text-primary')}>{value}</span>
    </div>
  );
}
